/**
 * Run a refresh callback when a tab the user has left comes back into use.
 *
 * Long-lived tabs are the normal case in WeaveIn: a case manager opens a member
 * and leaves it open across a meeting, a lunch break, or overnight. Anything
 * fetched once at mount is then arbitrarily old, and a deploy or a config change
 * in between is invisible until the user reloads the browser by hand.
 *
 * Two signals matter, and both mean "the user is about to look at this again":
 *
 *  - `visibilitychange` to visible — they switched back to the tab.
 *  - `online` — the connection came back after a drop.
 *
 * Throttled by `minIntervalMs` so alt-tabbing repeatedly does not turn into a
 * request per switch. The callback is held in a ref so callers can pass an
 * inline arrow without re-subscribing on every render.
 */
import { useEffect, useRef } from 'react';

interface Options {
  /** Minimum gap between two triggered refreshes. Default 30s. */
  minIntervalMs?: number;
  /** Set false to suspend without unmounting (e.g. while a form is dirty). */
  enabled?: boolean;
}

export default function useRefreshOnFocus(
  onRefresh: () => void,
  { minIntervalMs = 30_000, enabled = true }: Options = {}
): void {
  const callbackRef = useRef(onRefresh);
  callbackRef.current = onRefresh;

  const lastRunRef = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const maybeRefresh = () => {
      const now = Date.now();
      if (now - lastRunRef.current < minIntervalMs) return;
      lastRunRef.current = now;
      callbackRef.current();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') maybeRefresh();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('online', maybeRefresh);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('online', maybeRefresh);
    };
  }, [enabled, minIntervalMs]);
}
